import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const useProfile = (session: any) => {
  const [categories, setCategories] = useState<string[]>([]);
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [businessName, setBusinessName] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [businessWebsite, setBusinessWebsite] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const { toast } = useToast();

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      
      if (!session) {
        setIsLoading(false);
        return;
      }

      const { data: profile, error } = await supabase
        .from("professional_profiles")
        .select("*")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (error) {
        console.error("Error loading profile:", error);
        toast({
          title: "Error loading profile",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      if (profile) {
        setLatitude(profile.lat || null);
        setLongitude(profile.lng || null);
        setAddress(profile.address || "");
        setCategories(profile.categories || []);
        setBusinessName(profile.business_name || "");
        setBusinessPhone(profile.business_phone || "");
        setBusinessWebsite(profile.business_website || "");
        setBusinessDescription(profile.bio || "");
      } else {
        console.log("No profile found for user");
        toast({
          title: "Welcome!",
          description: "Please complete your profile to get started.",
        });
      }
    } catch (error) {
      console.error("Error in loadProfile:", error);
      toast({
        title: "Error",
        description: "Failed to load profile. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocationSelect = async (location: { 
    lat: number; 
    lng: number; 
    formattedAddress: string;
    placeId?: string;
    businessDetails?: {
      name?: string;
      phoneNumber?: string;
      website?: string;
      rating?: number;
      userRatingsTotal?: number;
      reviews?: google.maps.places.PlaceReview[];
      photoUrl?: string;
    };
  }) => {
    setLatitude(location.lat);
    setLongitude(location.lng);
    setAddress(location.formattedAddress);

    if (location.businessDetails) {
      if (location.businessDetails.name) {
        setBusinessName(location.businessDetails.name);
      }
      if (location.businessDetails.phoneNumber) {
        setBusinessPhone(location.businessDetails.phoneNumber);
      }
      if (location.businessDetails.website) {
        setBusinessWebsite(location.businessDetails.website);
      }
    }
  };

  const saveProfile = async () => {
    try {
      if (!session?.user?.id) {
        toast({
          title: "Error",
          description: "Please login to save your profile",
          variant: "destructive",
        });
        return;
      }

      const profileData = {
        user_id: session.user.id,
        lat: latitude,
        lng: longitude,
        address,
        categories,
        business_name: businessName,
        business_phone: businessPhone,
        business_website: businessWebsite,
        bio: businessDescription,
      };

      // First, check if a profile with this business name exists
      const { data: existingProfile, error: fetchError } = await supabase
        .from("professional_profiles")
        .select("id")
        .eq("business_name", businessName)
        .maybeSingle();

      if (fetchError) {
        console.error("Error checking existing profile:", fetchError);
        toast({
          title: "Error",
          description: "Failed to check existing profile",
          variant: "destructive",
        });
        return;
      }

      let error;
      if (existingProfile) {
        // Update existing profile
        const { error: updateError } = await supabase
          .from("professional_profiles")
          .update(profileData)
          .eq("id", existingProfile.id);
        error = updateError;
      } else {
        // Insert new profile
        const { error: insertError } = await supabase
          .from("professional_profiles")
          .insert(profileData);
        error = insertError;
      }

      if (error) {
        console.error("Error saving profile:", error);
        toast({
          title: "Error saving profile",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Profile saved successfully",
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error in saveProfile:", error);
      toast({
        title: "Error",
        description: "Failed to save profile. Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      saveProfile();
    } else {
      setIsEditing(true);
    }
  };

  return {
    categories,
    setCategories,
    address,
    latitude,
    longitude,
    isEditing,
    isLoading,
    businessName,
    businessPhone,
    businessWebsite,
    businessDescription,
    setBusinessName,
    setBusinessPhone,
    setBusinessWebsite,
    setBusinessDescription,
    loadProfile,
    handleLocationSelect,
    handleEditToggle,
    saveProfile
  };
};